import React, {
  useCallback,
  useMemo,
} from 'react'

import PropTypes from 'prop-types'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'

import { Button } from '@termly/react-components'

import combineStyle from 'utils/combineStyle'

import useSaveConsent from 'hooks/useSaveConsent'

import { useConsentConfig } from 'components/ConsentConfigProvider'
import { useDisplayManager } from 'components/DisplayManager'
import { useTCF } from 'components/TCFProvider'
import { useWebsite } from 'components/WebsiteProvider'

import TermlyLogo from 'assets/images/termly-logo.svg'

import ConsentMessage from './ConsentMessage'
import PreferencesButton from './PreferencesButton'

import Locale from './locale'
import Styles from './styles.scss'

const COOKIE_CONSENT_URL = 'https://termly.io/products/cookie-consent-manager/'


export default function ConsentPromptContent({ className, stylesOverrides }) {
  const consentConfig = useConsentConfig()
  const { closeAllUI } = useDisplayManager()
  const { gvl } = useTCF()
  const { showLogo } = useWebsite()

  const {
    acceptAll,
    declineAll,
  } = useSaveConsent()

  const handleAcceptAllClick = useCallback(() => {
    closeAllUI()

    acceptAll()
  }, [acceptAll, closeAllUI])

  const handleDeclineAllClick = useCallback(() => {
    closeAllUI()

    declineAll()
  }, [closeAllUI, declineAll])

  // There are two apps that need to know whether we should show the logo on the ConsentPrompt or not:
  // 1) Dashboard, for showing the BannerPreview.
  // 2) ResourceBlocker, for showing the actual ConsentPrompt/Banner on user's websites.
  //
  // Previously, we were only using the show_logo property on the website object from ResourceBlocker's local
  // WebsiteProvider but Dashboard doesn't know anything about this. So, our BannerPreviews in the dashboard
  // were always showing the "Powered by Termly" logo, even for paid users. So, we went into Dashboard and added
  // a show_logo property ( via isBasic from the useCurrentUserPlanQuery() ) to the consentConfig object. We then
  // pass that down into the ConsentPromptApp, which you see here as consentConfig.show_logo. But, when the ResourceBlocker
  // is loaded in isolation on an end user's website, it obviously doesn't run any of that dashboard stuff and doesn't know
  // anything about that handy-dandy new show_logo property.
  //
  // TLDR; We need both consentConfig.show_logo, for the dashboard banner preview, and showLogo, for the resource-blocker on end user's websites,
  // to determine whether or not to show the logo.
  const isLogoShown = ( consentConfig.show_logo || showLogo )

  const rootClassName = classnames(Styles.root,
                                   Styles[consentConfig.display_style],
                                   Styles[consentConfig.size],
                                   className,
                                   't-consentPrompt')

  const buttonClassName = classnames(Styles.button,
                                     Styles[consentConfig.display_style],
                                     Styles[consentConfig.size])

  const acceptButtonClassName = classnames(buttonClassName,
                                           't-acceptAllButton')

  const contentContainerClassName = classnames(Styles.main, stylesOverrides.main)

  const consentMessageClassName = classnames(Styles.message, stylesOverrides.message)

  const {
    btnStyle,
    previewStyle,
  } = combineStyle(consentConfig.theme)

  const buttonsContainerBackground = useMemo(() => ({
    background: previewStyle.background,
  }), [previewStyle])

  const buttonsContainerClassName = useMemo(() => {
    const showTCFButtons = ( gvl && consentConfig.display_style === 'banner' )
    const buttonStyles = ( showTCFButtons ) ? Styles.tcfButtons : Styles.buttons

    return classnames(buttonStyles, Styles[consentConfig.size])
  }, [consentConfig, gvl])

  return (
    <div
      aria-label="Cookie Consent Prompt"
      aria-modal="true"
      className={ rootClassName }
      role="dialog"
      style={ previewStyle }
    >
      <div className={ contentContainerClassName }>
        {
          renderLogo({
            isLogoShown,
            stylesOverrides,
          })
        }

        <div className={ stylesOverrides.content }>
          <div className={ consentMessageClassName }>
            <ConsentMessage />
          </div>

          <div
            className={ buttonsContainerClassName }
            style={ buttonsContainerBackground }
          >
            {
              renderPreferencesButton({
                buttonClassName,
                btnStyle,
                show: consentConfig.enable_cookie_preference_button,
              })
            }

            {
              renderDeclineButton({
                buttonClassName,
                btnStyle,
                handleClick: handleDeclineAllClick,
                show: consentConfig.enable_decline_button,
              })
            }

            <Button
              className={ acceptButtonClassName }
              color="primary"
              data-tid="banner-accept"
              handleClick={ handleAcceptAllClick }
              style={ btnStyle }
              type="solid"
            >
              <FormattedMessage
                { ...Locale.accept }
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

ConsentPromptContent.propTypes = {
  className: PropTypes.string,
  stylesOverrides: PropTypes.shape({
    content: PropTypes.string,
    logo: PropTypes.string,
    main: PropTypes.string,
    message: PropTypes.string,
  }),
}

ConsentPromptContent.defaultProps = {
  stylesOverrides: {},
}

function renderLogo({ isLogoShown, stylesOverrides }) {
  if ( !isLogoShown ) {
    return null
  }

  const logoClassName = classnames(Styles.logo, stylesOverrides.logo)

  return (
    <div className={ logoClassName }>
      <a
        href={ COOKIE_CONSENT_URL }
        rel="noreferrer"
        target="_blank"
      >
        <img
          alt="Powered by Termly"
          src={ TermlyLogo }
        />
      </a>
    </div>
  )
}

function renderPreferencesButton({ buttonClassName, btnStyle, show }) {
  if ( !show ) {
    return null
  }

  return (
    <PreferencesButton
      btnStyle={ btnStyle }
      className={ buttonClassName }
    />
  )
}

function renderDeclineButton({ buttonClassName, btnStyle, handleClick, show }) {
  if ( !show ) {
    return null
  }

  const className = classnames(buttonClassName, 't-declineButton')

  return (
    <Button
      className={ className }
      color="primary"
      data-tid="banner-decline"
      data-tracking="cookieDeclined"
      handleClick={ handleClick }
      style={ btnStyle }
      type="solid"
    >
      <FormattedMessage
        { ...Locale.decline }
      />
    </Button>
  )
}
